import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, MapPin, Search, Heart, ShoppingBag, HeartHandshake } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Help() {
  const [needs, setNeeds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [city, setCity] = useState("");
  const [selected, setSelected] = useState(null);
  const [donor, setDonor] = useState({ donor_name: "", donor_email: "" });
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async (filterCity) => {
    const [n, r] = await Promise.all([
      api.get("/needs/public", { params: filterCity ? { city: filterCity } : {} }),
      api.get("/requests/public"),
    ]);
    setNeeds(n.data);
    setRequests(r.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/help/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Thank you! 💚 Your gift is being purchased and shipped to the organization.");
          setParams({}, { replace: true });
          load(city);
          return;
        }
        if (data.status === "expired") { setParams({}, { replace: true }); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const confirmHelp = async () => {
    setLoading(true);
    try {
      const payload = { origin_url: window.location.origin, ...donor };
      const { data } = selected._kind === "request"
        ? await api.post("/requests/checkout", { request_id: selected.id, ...payload })
        : await api.post("/help/checkout", { need_id: selected.id, ...payload });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-8">
        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-orange-700 bg-orange-50 px-4 py-1.5 rounded-full">
          <Heart className="w-3.5 h-3.5" /> Help someone today
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mt-6 max-w-3xl">
          Buy exactly what a local aid organization needs — right now.
        </h1>
        <p className="text-stone-600 mt-4 max-w-2xl leading-relaxed">
          These are real items requested by aid organizations, ordered by most urgent. Pick one, click <span className="font-medium text-stone-900">Help!</span>, and pay securely.
          A local aid organization orders it from Amazon, and the recipient picks it up there.
        </p>

        <div className="flex gap-3 mt-8 max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="help-city-filter" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Filter by city…" className="rounded-full pl-9 h-11 border-stone-200 bg-white" />
          </div>
          <Button data-testid="help-city-search" onClick={() => load(city)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white px-6 h-11">Search</Button>
        </div>
      </section>

      {/* People who need help right now */}
      {requests.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-orange-600 animate-pulse" />
            <h2 className="font-heading text-2xl font-medium tracking-tight text-stone-900">People who need help right now</h2>
          </div>
          <p className="text-stone-500 mt-1">First come, first served — fund an item and a local aid organization orders it from Amazon for the person to pick up there.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="help-requests-grid">
            {requests.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-orange-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center"><Heart className="w-5 h-5 text-orange-700" /></div>
                  <Badge variant="outline" className="rounded-full capitalize">{r.category}</Badge>
                </div>
                <h3 className="font-heading text-lg font-medium text-stone-900 mt-4">{r.item_name}</h3>
                <p className="text-sm text-stone-500 mt-1">Requested by {r.requester_name}</p>
                <p className="flex items-center gap-1 text-sm text-stone-500 mt-1"><MapPin className="w-3.5 h-3.5" /> Pickup: {r.pickup_org_name}{r.city ? `, ${r.city}` : ""}</p>
                {r.note && <p className="text-sm text-stone-500 mt-2 italic">"{r.note}"</p>}
                <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between">
                  <span className="font-heading text-2xl font-semibold text-emerald-800">${Number(r.price).toFixed(2)}</span>
                  <Button data-testid={`help-req-${r.id}`} onClick={() => { setSelected({ ...r, _kind: "request", estimated_price: r.price, org_name: r.pickup_org_name }); setDonor({ donor_name: "", donor_email: "" }); }} className="rounded-full bg-orange-700 hover:bg-orange-800 text-white">
                    <Gift className="w-4 h-4 mr-2" /> Help this person
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Needs grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <h2 className="font-heading text-2xl font-medium tracking-tight text-stone-900 mb-6">Organization wishlists</h2>
        {needs.length === 0 && requests.length === 0 ? (
          <div data-testid="give-directly-cta" className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-10 sm:p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-800 text-white flex items-center justify-center mx-auto">
              <HeartHandshake className="w-7 h-7" />
            </div>
            <h3 className="font-heading text-2xl font-semibold text-stone-900 mt-5">Don't see any requests right now?</h3>
            <p className="text-stone-600 mt-3 max-w-xl mx-auto leading-relaxed">
              Donate funds or supplies directly to an organization of your choosing. Your gift goes straight to the organization you pick.
            </p>
            <Link to="/give">
              <Button data-testid="give-directly-btn" className="mt-6 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-8">
                <HeartHandshake className="w-4 h-4 mr-2" /> Donate directly to an organization
              </Button>
            </Link>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="help-needs-grid">
          {needs.length === 0 && (
            <div className="col-span-full bg-white rounded-2xl border border-dashed border-stone-300 p-12 text-center text-stone-500">
              No open wishlists right now.
            </div>
          )}
          {needs.map((n) => {
            const pct = n.quantity_needed ? Math.min(100, Math.round((n.quantity_fulfilled / n.quantity_needed) * 100)) : 0;
            return (
              <div key={n.id} className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-orange-700" /></div>
                  <Badge variant="outline" className="rounded-full capitalize">{n.category}</Badge>
                </div>
                <h3 className="font-heading text-lg font-medium text-stone-900 mt-4">{n.item_name}</h3>
                <p className="flex items-center gap-1 text-sm text-stone-500 mt-1"><MapPin className="w-3.5 h-3.5" /> {n.org_name}{n.city ? `, ${n.city}` : ""}</p>
                {n.description && <p className="text-sm text-stone-500 mt-2 italic">"{n.description}"</p>}
                <div className="mt-4">
                  <Progress value={pct} className="h-2" />
                  <p className="text-xs text-stone-500 mt-1.5">{n.quantity_fulfilled} of {n.quantity_needed} fulfilled</p>
                </div>
                <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between">
                  <span className="font-heading text-2xl font-semibold text-emerald-800">${Number(n.estimated_price).toFixed(2)}</span>
                  <Button data-testid={`help-btn-${n.id}`} onClick={() => { setSelected(n); setDonor({ donor_name: "", donor_email: "" }); }} className="rounded-full bg-orange-700 hover:bg-orange-800 text-white">
                    <Gift className="w-4 h-4 mr-2" /> Help!
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </section>

      {/* Charity note */}
      <section className="bg-emerald-900 text-emerald-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 text-center">
          <p className="text-sm text-emerald-200/80 max-w-2xl mx-auto">
            Gather is <span className="font-medium text-white">free to use with no login or email</span> for aid organizations and individuals.
            Every payment on this page is directed to <span className="font-medium text-white">Partnership to End Addiction</span>.
          </p>
        </div>
      </section>

      {/* Donor dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">You're helping with: {selected?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              You'll pay <span className="font-medium text-stone-900">${Number(selected?.estimated_price || 0).toFixed(2)}</span> securely via Stripe.
              <span className="font-medium">{selected?.org_name}</span> orders this item from Amazon, and the recipient picks it up there.
            </p>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Your name</Label>
              <Input data-testid="donor-name" value={donor.donor_name} onChange={(e) => setDonor((d) => ({ ...d, donor_name: e.target.value }))} placeholder="Jane Doe" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Email (optional)</Label>
              <Input data-testid="donor-email" type="email" value={donor.donor_email} onChange={(e) => setDonor((d) => ({ ...d, donor_email: e.target.value }))} placeholder="jane@email.com" className="rounded-xl h-11" />
            </div>
            <p className="text-xs text-stone-400">The local aid organization places the Amazon order after payment; the recipient collects it at that organization.</p>
          </div>
          <DialogFooter>
            <Button data-testid="donor-confirm" onClick={confirmHelp} disabled={loading} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11">
              {loading ? "Redirecting to payment…" : `Donate $${Number(selected?.estimated_price || 0).toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
